import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BadgeCheck, Loader2, Users, Inbox, Sparkles } from "lucide-react";
import { API, getAuthHeaders } from "@/App";

export default function PremiumUpgradeDialog({ open, onOpenChange }) {
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/subscriptions/me`, { credentials: "include", headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then(setSub);
  }, [open]);

  const subscribe = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/subscriptions/business/checkout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.detail || "Error");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.message || "No se pudo iniciar el pago");
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/subscriptions/business/cancel`, {
        method: "POST",
        credentials: "include",
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      toast.success("Tu suscripción se cancelará al final del periodo");
      setSub({ ...sub, status: "cancel_at_period_end" });
    } catch (e) {
      toast.error(e.message || "No se pudo cancelar");
    } finally {
      setBusy(false);
    }
  };

  const isActive = sub && sub.active;
  const price = sub?.price_mxn ?? 30;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#17212b] border-[#232e3c] text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BadgeCheck className="w-5 h-5 text-blue-400" /> OkChat for Business
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Verifica tu cuenta y desbloquea funciones premium.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-lg bg-gradient-to-br from-blue-600/20 to-purple-600/20 border border-blue-500/30 p-4">
            <div className="text-3xl font-bold">${price.toFixed(0)} <span className="text-base font-normal text-slate-300">MXN / mes</span></div>
            <ul className="mt-3 space-y-2 text-sm">
              <li className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-blue-400" /> Insignia azul de verificación</li>
              <li className="flex items-center gap-2"><Inbox className="w-4 h-4 text-purple-400" /> Bandeja compartida del equipo</li>
              <li className="flex items-center gap-2"><Users className="w-4 h-4 text-emerald-400" /> Hasta 10 agentes</li>
              <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-pink-400" /> Stickers ilimitados + mini-apps</li>
            </ul>
          </div>

          {isActive ? (
            <>
              <div className="text-sm text-emerald-400">
                ✓ Tu cuenta está verificada. Estado: {sub.status}
                {sub.renews_at && ` · renueva ${new Date(sub.renews_at).toLocaleDateString()}`}
              </div>
              {sub.status !== "cancel_at_period_end" && (
                <Button onClick={cancel} disabled={busy} variant="outline" className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10">
                  Cancelar suscripción
                </Button>
              )}
            </>
          ) : (
            <Button onClick={subscribe} disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BadgeCheck className="w-4 h-4 mr-2" />}
              Suscribirme · ${price.toFixed(0)} MXN
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
